import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsMigrationAssessment, ExcludedServers } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box,Container, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { TcoContext } from '../../contexts/TcoContext';
import PreviewBanner from '../common/PreviewBanner';
import { ErrorMsgFormat } from '../tco/error-handler-context/CommonTcoErrorHandler';
import { generateExcludedServersErrorMsgs } from '../tco/error-handler-context/helper';
import GenerateErrorMessageBox from '../tco/error-handler-context/TcoErrorHandler';
import { completionPlanDistributionTime, DEFAULT_MIGRATION_TRANCHE_IN_MONTHS } from './constants';
import { DistributionTime } from './interfaces';
import MigrationCashflowCompletionPlan from './MigrationCashflowCompletionPlan';
import MigrationCashflowCostDetails from './MigrationCashflowCostDetails';
import MigrationCashflowGraph from './MigrationCashflowGraph';

const tcoService = new TcoService();

function MigrationCashflow({ flash }: FlashContextInterface) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    const portfolioId = match?.params?.portfolioId;

    const { assessment, tcoLoading } = useContext(TcoContext);

    const [ migrationAssessmentLoading, setMigrationAssessmentLoading ] = useState(false);
    const [ migrationAssessment, setMigrationAssessment ] = useState<AwsMigrationAssessment | null>(null);
    const [ trancheUnit, setTrancheUnit ] = useState<DistributionTime>(completionPlanDistributionTime[0]);
    const [ excludedServers, setExcludedServers ] = useState<ExcludedServers>({outOfScope: 0});
    const { panelHandler } = useContext(HelpPanelContext);

    const history = useHistory();

    const isMounted = useIsMounted();


    const loadMigrationAssessment = async () => {
        setMigrationAssessmentLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const migrationAssessment = await tcoService.getAwsMigration(tcoAssessmentId);
                if (isMounted()) {
                    setMigrationAssessment(migrationAssessment);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setMigrationAssessmentLoading(false);
            }
        }
    };

    const loadExcludedServers = async () => {
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const summary = await tcoService.getSummary(tcoAssessmentId);
                if (isMounted()) {
                    setExcludedServers(summary.output.excludedServers);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.MIGRATION_CASH_FLOW);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadMigrationAssessment();
            loadExcludedServers();
            setDistributionTime(assessment.input.migration.migrationTranche ?? DEFAULT_MIGRATION_TRANCHE_IN_MONTHS);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const setDistributionTime = (tranchePeriod: number) => {
        const tranche = completionPlanDistributionTime.find(item => item.unitInMonths === tranchePeriod);
        if (tranche) {
            setTrancheUnit(tranche);
        }
    };

    const cashflowGraph =  migrationAssessment ? (
        <MigrationCashflowGraph 
            currency={migrationAssessment.output.migration.currency}
            roi={migrationAssessment.output.migration.roi}
            trancheUnit={trancheUnit}
        />
    ) : null;

    const completionPlan = assessment ? (
        <MigrationCashflowCompletionPlan
            migrationTranche={trancheUnit}
        />
    ) : null;

    const costDetails = migrationAssessment ? (
        <MigrationCashflowCostDetails
            migration={migrationAssessment.output.migration}
        />
    ) : null;

    const errorMessages: ErrorMsgFormat[] = portfolioId ? generateExcludedServersErrorMsgs(excludedServers, portfolioId) : [];

    return (
        <Container>
            <PreviewBanner />
            <Box variant='h1'>{constants.MIGRATION_CASH_FLOW_HEADER}</Box>
            <GenerateErrorMessageBox
                errorMessages={errorMessages}
                history={history}
            />
            <SpaceBetween size='s'>
                { migrationAssessmentLoading || tcoLoading ? (
                    <Spinner size='large'/>
                ) : (
                    <SpaceBetween size='m'>
                        {cashflowGraph}
                        {completionPlan}
                        {costDetails}
                    </SpaceBetween> 
                )}
            </SpaceBetween>
        </Container>
    );
}

export default withFlashContext(MigrationCashflow);
